// ============================================================================
// Statement Handlers - Type inference for statements
// ============================================================================

import fs from 'fs';
import path from 'path';
import { isStdlibImport, resolveStdlibPath } from '../../stdlib.js';
import { resolveTypes, pushToParent, visitChildren, visit } from '../visitor.js';
import { parseTypeExpression, parseGenericParams, parseGenericConstraints, resolveTypeAlias, isTypeCompatible, getPropertyType, getAnnotationType, ArrayType, ObjectType, getBaseTypeOfLiteral, createUnionType } from '../typeSystem.js';
import { UndefinedType, StringType, NumberType, LiteralType, UnionType } from '../Type.js';
import { detectTypeofCheck, detectEqualityCheck, detectTruthinessCheck, detectPredicateGuard, applyIfBranchGuard, applyElseBranchGuard, applyPostIfGuard, detectImpossibleComparison } from '../typeGuards.js';
import TypeChecker from '../typeChecker.js';
import parser from '../../parser.js';
import { tokensDefinition } from '../../tokensDefinition.js';
import backend from '../../backend.js';
import { AnyType, AnyFunctionType, FunctionType } from '../Type.js';
import { runBindingPhase } from '../symbolTable.js';

/**
 * Extract import name nodes from destructuring_values node
 * @param {Object} node - destructuring_values AST node
 * @returns {Array<{name: string, node: Object}>} Array of {name, node} pairs
 */
function extractImportNameNodes(node) {
  const entries = [];

  function traverse(n) {
    if (!n) return;

    if (n.type === 'destructuring_values') {
      if (n.named.name) {
        entries.push({ name: n.named.name.value, node: n.named.name });
      }
      if (n.named.more) {
        traverse(n.named.more);
      }
    }
  }

  traverse(node);
  return entries;
}

/**
 * Collect all property names from an exp:path tree (for LHS of property assignment).
 * For `a.b.c`, walks the nested exp nodes and collects [a, b, c], returning "b.c"
 * (skipping the base variable name — only the property chain part).
 */
function collectPropertyPathFromExp(expNode) {
  const parts = [];
  function walk(node) {
    if (!node) return;
    if (node.type === 'exp') {
      if (node.named?.prop) {
        // Inlined property access: recurse into obj first, then add this prop
        walk(node.named.obj);
        parts.push(node.named.prop.value);
      } else {
        for (const child of node.children ?? []) {
          if (child.type === 'exp') walk(child);
        }
      }
    }
  }
  walk(expNode);
  return parts.join('.');
}

/**
 * Widen literal types to their abstract base types
 * For LiteralType, returns baseType; for UnionType, widens each member
 */
function widenLiteralTypes(type) {
  if (type instanceof LiteralType) {
    return type.baseType;
  } else if (type instanceof UnionType) {
    const widenedTypes = type.types.map(t => widenLiteralTypes(t));
    // Avoid creating nested unions by flattening
    const flattened = [];
    for (const t of widenedTypes) {
      if (t instanceof UnionType) {
        flattened.push(...t.types);
      } else {
        flattened.push(t);
      }
    }
    return new UnionType(flattened);
  }
  return type;
}

/**
 * Extract destructuring bindings recursively from destructuring_values node.
 * Returns array of {propertyPath, propertyName, varName, node, annotationNode}.
 *
 * `propertyPath` is an ordered array of property keys needed to reach the bound
 * variable from the RHS object, e.g. ['info', 'score'] for `{ info: { score } }`.
 * `propertyName` (the last element of propertyPath) is retained for backward compat.
 */
function extractDestructuringBindings(node, parentPath = []) {
  const bindings = [];
  
  if (node.type === 'destructuring_values') {
    if (node.named.nested) {
      // Nested destructuring: e.g. `info: { score, rank }`
      // `node.named.name` is the intermediate property; descend into the nested pattern.
      const intermediateProp = node.named.name.value;
      const nestedValues = node.named.nested.named?.values;
      if (nestedValues) {
        bindings.push(...extractDestructuringBindings(nestedValues, [...parentPath, intermediateProp]));
      }
    } else if (node.named.name && node.named.rename) {
      // Renamed: x as xPos
      bindings.push({
        propertyPath: [...parentPath, node.named.name.value],
        propertyName: node.named.name.value,
        varName: node.named.rename.value,
        node: node.named.rename,
        annotationNode: null,
      });
    } else if (node.named.name) {
      // Simple name or typed: attributes, or attributes: DogGameProps
      bindings.push({
        propertyPath: [...parentPath, node.named.name.value],
        propertyName: node.named.name.value,
        varName: node.named.name.value,
        node: node.named.name,
        annotationNode: node.named.annotation || null,
      });
    }
    // Recurse for siblings through 'more' (same parent path level)
    if (node.named.more) {
      bindings.push(...extractDestructuringBindings(node.named.more, parentPath));
    } else if (node.children) {
      // Find nested destructuring_values in children
      for (const child of node.children) {
        if (child.type === 'destructuring_values') {
          bindings.push(...extractDestructuringBindings(child, parentPath));
        }
      }
    }
  } else if (node.children) {
    // For other container nodes, recurse through children
    for (const child of node.children) {
      if (child.type === 'destructuring_values') {
        bindings.push(...extractDestructuringBindings(child, parentPath));
      }
    }
  }
  
  return bindings;
}

/**
 * Get the current return type count for a function scope
 */
function getReturnTypeCount(functionScope) {
  return functionScope?.__returnTypes?.length || 0;
}

function createStatementHandlers(getState) {
  return {
    GLOBAL_STATEMENT: resolveTypes,
    SCOPED_STATEMENTS: resolveTypes,
    
    SCOPED_STATEMENT: (node, parent) => {
      const { getFunctionScope, pushWarning, typeAliases, inferencePhase } = getState();
      
      // Check if this is a return statement by looking at the first child
      if (node.children && node.children[0] && node.children[0].type === 'return') {
        const functionScope = getFunctionScope();
        if (functionScope && functionScope.__returnTypes) {
          // Visit children to get type inference on expressions
          visitChildren(node);
          
          // Find the exp child and get its type
          let returnType = UndefinedType;
          let returnExpNode = null;
          for (const child of node.children) {
            if (child.type === 'exp') {
              returnExpNode = child;
              if (child.inference && child.inference.length > 0) {
                // Take the last inference value, which is the final result after all operations
                returnType = child.inference[child.inference.length - 1];
              }
              break;
            }
          }

          // Validate each return expression against the declared return type.
          // Doing this per-statement avoids the problem where any-typed returns
          // contaminate the end-of-function union check.
          if (inferencePhase === 'checking' && returnExpNode) {
            const declaredReturnType = functionScope.__declaredReturnType;
            if (declaredReturnType && returnType !== AnyType) {
              if (!isTypeCompatible(returnType, declaredReturnType, typeAliases)) {
                const displayType = getBaseTypeOfLiteral(returnType);
                // Use the original annotation type for the message (predicate functions store
                // BooleanType in __declaredReturnType for checking, but the annotation itself
                // is the predicate — e.g. "x is string").
                const displayDeclared = functionScope.__annotationReturnType ?? declaredReturnType;
                pushWarning(
                  returnExpNode,
                  `returns ${displayType} but declared as ${displayDeclared}`
                );
              }
            }
          }
          
          functionScope.__returnTypes.push(returnType);
          pushToParent(node, parent);
          return;
        }
      }
      
      // Not a return statement, handle normally
      resolveTypes(node);
      pushToParent(node, parent);
    },
    
    type_alias: (node, parent) => {
      const { typeAliases } = getState();
      
      // Extract the alias name and its type
      const aliasName = node.named.name.value;
      const aliasType = parseTypeExpression(node.named.type);
      
      // Parse generic parameters and constraints if present
      const genericParams = node.named.generic_params 
        ? parseGenericParams(node.named.generic_params)
        : [];
      const genericConstraints = node.named.generic_params
        ? parseGenericConstraints(node.named.generic_params)
        : null;
      
      // Store the type alias
      if (genericParams.length > 0) {
        // Store as generic type alias with parameters
        typeAliases[aliasName] = {
          type: aliasType,
          genericParams,
          genericConstraints: genericConstraints?.size > 0 ? genericConstraints : null,
        };
      } else {
        // Store as regular type alias (maintain backward compatibility)
        typeAliases[aliasName] = aliasType;
      }
      
      // Type aliases don't produce values, so don't push to parent
    },

    import_statement: (node) => {
      const { typeAliases, currentFilename } = getState();
      
      // If we don't have a filename context, we can't resolve imports
      if (!currentFilename) {
        return;
      }
      
      // Extract the file being imported
      const fileNode = node.named.file || node.named.module;
      if (!fileNode) {
        return;
      }
      
      const importPath = fileNode.value.slice(1, -1); // Remove quotes
      
      // Only process .blop file imports (skip node_modules, etc.)
      if (!importPath.startsWith('.') && !isStdlibImport(importPath)) {
        return;
      }
      
      try {
        // Resolve the import path relative to current file
        const resolvedPath = isStdlibImport(importPath)
          ? resolveStdlibPath(importPath)
          : path.resolve(path.dirname(currentFilename), importPath);
        
        // Check if it's a .blop file
        if (!resolvedPath.endsWith('.blop')) {
          return;
        }
        
        // Check if file exists
        if (!fs.existsSync(resolvedPath)) {
          return;
        }
        
        // Load and parse the imported file to extract its type definitions
        const importedSource = fs.readFileSync(resolvedPath, 'utf8');
        
        const tokenStream = parser.tokenize(tokensDefinition, importedSource);
        const tree = parser.parse(tokenStream);
        
        if (tree.success) {
          // Generate backend to extract type definitions
          const result = backend.generateCode(tree, tokenStream, importedSource, resolvedPath);
          
          // Run binding phase on imported tree to get function definitions
          const importedSymbolTable = runBindingPhase(tree);
          const importedSymbols = importedSymbolTable.getAllSymbols();
          const importedFunctions = importedSymbols.functions;
          const importedClasses = importedSymbolTable.getClasses();

          if (result.typeAliases) {
            // Check what's being imported
            if (node.named.dest_values) {
              // import { User, Post } from './types.blop'
              // Only import specific types
              const importNameNodes = extractImportNameNodes(node.named.dest_values);
              const { getCurrentScope, inferencePhase } = getState();
              const scope = getCurrentScope();
              importNameNodes.forEach(({ name, node: nameNode }) => {
                if (result.typeAliases[name] && result.typeAliases[name].typeNode) {
                  // Parse the type definition for use in inference
                  const aliasType = parseTypeExpression(result.typeAliases[name].typeNode);
                  typeAliases[name] = aliasType;
                  if (inferencePhase === 'inference' && nameNode.inferredType === undefined) {
                    nameNode.inferredType = aliasType;
                  }
                } else if (importedFunctions[name]) {
                  // Register imported function in current scope for hover + lookupVariable
                  const def = importedFunctions[name];
                  scope[name] = def;
                  if (inferencePhase === 'inference' && nameNode.inferredType === undefined) {
                    nameNode.inferredType = def.params
                      ? new FunctionType(def.params, def.type ?? AnyType, def.genericParams ?? [], def.paramNames ?? [])
                      : AnyFunctionType;
                  }
                } else if (importedClasses[name]) {
                  // Register imported class in current scope
                  const def = importedClasses[name];
                  scope[name] = def;
                  if (inferencePhase === 'inference' && nameNode.inferredType === undefined) {
                    nameNode.inferredType = def.type;
                  }
                }
              });
            } else if (node.named.name && !node.named.module) {
              // import User from './types.blop'
              const name = node.named.name.value;
              const nameNode = node.named.name;
              const { inferencePhase } = getState();
              if (result.typeAliases[name] && result.typeAliases[name].typeNode) {
                const aliasType = parseTypeExpression(result.typeAliases[name].typeNode);
                typeAliases[name] = aliasType;
                if (inferencePhase === 'inference' && nameNode.inferredType === undefined) {
                  nameNode.inferredType = aliasType;
                }
              } else if (importedFunctions[name]) {
                const def = importedFunctions[name];
                const { getCurrentScope } = getState();
                getCurrentScope()[name] = def;
                if (inferencePhase === 'inference' && nameNode.inferredType === undefined) {
                  nameNode.inferredType = def.params
                    ? new FunctionType(def.params, def.type ?? AnyType, def.genericParams ?? [], def.paramNames ?? [])
                    : AnyFunctionType;
                }
              } else if (importedClasses[name]) {
                const def = importedClasses[name];
                const { getCurrentScope } = getState();
                getCurrentScope()[name] = def;
                if (inferencePhase === 'inference' && nameNode.inferredType === undefined) {
                  nameNode.inferredType = def.type;
                }
              }
            } else {
              // import './types.blop' - import all types
              // or import './types.blop' as types - can't use types directly
              // For now, skip these cases
            }

            // Always merge all type aliases from the imported file into the
            // current file's typeAliases so that signatures of imported
            // functions/classes that reference them (e.g. Route in add(route: Route))
            // can be fully resolved in the consuming file.
            const importedTypeAliases = importedSymbolTable.getAllSymbols().typeAliases;
            for (const [aliasName, aliasValue] of Object.entries(importedTypeAliases)) {
              if (!typeAliases[aliasName]) {
                typeAliases[aliasName] = aliasValue;
              }
            }
          }
        }
      } catch (error) {
        // Silently ignore errors in import resolution for inference
        // The backend will report actual import errors
      }
    },
    
    assign: (node, parent) => {
      const { pushInference, lookupVariable, typeAliases, pushWarning, stampTypeAnnotation, getCurrentScope } = getState();
      
      if (node.named.destructuring) {
        // Handle destructuring assignment: { total, text } = attributes
        // Optional annotation: { a, b }: MyType = expr
        if (node.named.annotation) {
          stampTypeAnnotation(node.named.annotation);
        }
        visit(node.named.exp, node);
        const expNode = node.named.exp;
        // The type may be on expNode.inference (when visited via resolveTypes) OR
        // on node.inference (when the exp is a name_exp visited directly as a child,
        // which pushes its result to the parent statement node).
        const expValueType = expNode?.inference?.[0] || node.inference?.[0];
        // Annotation overrides the inferred RHS type for property lookup purposes
        const annotationType = node.named.annotation ? getAnnotationType(node.named.annotation) : null;
        const valueType = annotationType || expValueType;
        
        const destNode = node.named.destructuring.named.values;
        const bindings = extractDestructuringBindings(destNode);

        // Stamp any inline annotations for hover support (independent of rhs type)
        for (const { annotationNode } of bindings) {
          if (annotationNode) stampTypeAnnotation(annotationNode);
        }

        if (valueType && valueType !== AnyType) {
          const resolvedValueType = resolveTypeAlias(valueType, typeAliases);
          
          // Extract destructured variable names and stamp them with property types
          if (resolvedValueType instanceof ObjectType) {
            for (const {propertyPath, propertyName, varName, node: varNode, annotationNode} of bindings) {
              if (annotationNode) {
                // Inline type annotation overrides the inferred property type
                const annotationType = getAnnotationType(annotationNode);
                if (annotationType) {
                  varNode.inferredType = annotationType;
                  // Update the live scope so subsequent lookups of this variable
                  // (e.g. as the rhs of another destructuring) use the declared type.
                  getCurrentScope()[varName] = { type: annotationType };
                }
              } else {
                // Walk propertyPath using raw property map to preserve optional markers.
                // getPropertyType() strips nullish/undefined which would drop optional?.
                let leafType = null;
                let currentObjType = resolvedValueType;

                for (const propName of propertyPath) {
                  const resolved = resolveTypeAlias(currentObjType, typeAliases);
                  if (resolved instanceof ObjectType) {
                    const prop = resolved.properties.get(propName);
                    if (prop) {
                      leafType = prop.type;
                      if (prop.optional && leafType) {
                        leafType = new UnionType([leafType, UndefinedType]);
                      }
                      currentObjType = prop.type;
                    } else {
                      leafType = null;
                      break;
                    }
                  } else {
                    leafType = null;
                    break;
                  }
                }

                if (leafType !== null) {
                  // Widen literal types to their abstract base types
                  const widenedType = widenLiteralTypes(leafType);
                  varNode.inferredType = widenedType;
                } else {
                  pushWarning(
                    destNode,
                    `Property '${propertyName}' does not exist on type ${valueType}`
                  );
                }
              }
            }
          }
        } else {
          // rhs type is unknown — still stamp any inline annotations
          for (const { varName, node, annotationNode } of bindings) {
            if (annotationNode) {
              const annotationType = getAnnotationType(annotationNode);
              if (annotationType) {
                node.inferredType = annotationType;
                getCurrentScope()[varName] = { type: annotationType };
              }
            }
          }
        }
        
        pushToParent(node, parent);
        pushInference(parent, node);
      } else if (node.named.name || node.named.path) {
        if (node.named.annotation) {
          // Stamp the type annotation for hover support
          stampTypeAnnotation(node.named.annotation);
        }
        
        visit(node.named.exp, node);
        
        // Check if this is a property assignment via exp:path (e.g., u.name = 1 or u.b.c = x)
        if (node.named.path && !node.named.name) {
          // Visit the LHS exp so __objectType/__propertyName get stamped by handlePropertyAccess
          visit(node.named.path, node);

          // After inlining object_access into exp, the LHS path exp itself carries
          // __objectType and __propertyName directly (stamped by handlePropertyAccess).
          const pathExpNode = node.named.path;

          if (pathExpNode && pathExpNode.__objectType !== undefined && pathExpNode.__propertyName) {
            const objectType = pathExpNode.__objectType;
            const propertyName = pathExpNode.__propertyName;

            const valueType = node.named.exp?.inference?.[0];

            if (valueType && valueType !== AnyType) {
              // For class instances, skip validation for non-declared (constructor-assigned) props
              const resolvedObjType = resolveTypeAlias(objectType, typeAliases);
              const shouldSkipValidation =
                resolvedObjType?.isClassInstance &&
                !resolvedObjType.properties.has(propertyName);

              if (!shouldSkipValidation) {
                const expectedType = getPropertyType(objectType, propertyName, typeAliases);
                // Collect full property path string for error messages (e.g. "user.userType")
                const fullPath = collectPropertyPathFromExp(pathExpNode);

                if (expectedType === null) {
                  pushWarning(node, `Property '${fullPath}' does not exist on type ${objectType}`);
                } else {
                  const resolvedExpectedType = resolveTypeAlias(expectedType, typeAliases);
                  if (!isTypeCompatible(valueType, resolvedExpectedType, typeAliases)) {
                    pushWarning(node, `Cannot assign ${valueType} to property '${fullPath}' of type ${expectedType}`);
                  }
                }
              }
            }
          }
        } else if (node.named.name && !node.named.path) {
          // Simple variable assignment: stamp the variable name  
          // At definition site, prefer declared annotation type over inferred type
          if (node.named.annotation && node.named.name.inferredType === undefined) {
            const annotationType = getAnnotationType(node.named.annotation);
            if (annotationType) {
              node.named.name.inferredType = annotationType;
            }
          } else {
            // No annotation, use inferred type from the expression
            const expNode = node.named.exp;
            const valueType = expNode && expNode.inference && expNode.inference[0];
            
            if (valueType && valueType !== AnyType && node.named.name.inferredType === undefined) {
              node.named.name.inferredType = resolveTypeAlias(valueType, typeAliases);
            }
          }
        }
        
        pushToParent(node, parent);
        pushInference(parent, node);
      }
    },
    
    condition: (node, parent) => {
      const { pushScope, popScope, lookupVariable, getCurrentScope, getFunctionScope, pushWarning, typeAliases } = getState();
      const functionScope = getFunctionScope();
      
      // Check for impossible comparisons
      const impossibleComparison = detectImpossibleComparison(node.named.exp, lookupVariable, typeAliases);
      if (impossibleComparison) {
        const { variable, comparedValue, possibleValues } = impossibleComparison;
        pushWarning(
          node.named.exp,
          `This condition will always be false: '${variable}' has type ${possibleValues.join(' | ')} and can never equal ${comparedValue}`
        );
      }

      const returnsBeforeIf = getReturnTypeCount(functionScope);

      // Visit condition expression FIRST. Predicate call nodes need inferredType stamped
      // before detectPredicateGuard can identify them.
      if (node.named.exp) {
        visit(node.named.exp, node);
      }

      // Detect type guard (syntax-based, then predicate which requires inferredType)
      const typeGuard = detectTypeofCheck(node.named.exp) || detectEqualityCheck(node.named.exp)
        || detectTruthinessCheck(node.named.exp) || detectPredicateGuard(node.named.exp);
      
      // Visit if branch (with type-narrowing scope when a type guard is present)
      if (typeGuard) {
        const ifScope = pushScope();
        applyIfBranchGuard(ifScope, typeGuard, lookupVariable);
        node.named.stats?.forEach(stat => visit(stat, node));
        popScope();
      } else {
        node.named.stats?.forEach(stat => visit(stat, node));
      }

      const returnsAfterIf = getReturnTypeCount(functionScope);

      // Visit else/elseif branch with type narrowing
      const elseNode = node.named.elseif;
      const isSimpleElse = elseNode && !elseNode.named?.exp && elseNode.named?.stats?.length > 0;

      if (isSimpleElse) {
        if (typeGuard) {
          const elseScope = pushScope();
          applyElseBranchGuard(elseScope, typeGuard, lookupVariable);
          elseNode.named.stats.forEach(stat => visit(stat, node));
          popScope();
        } else {
          visit(elseNode, node);
        }
      } else if (elseNode) {
        if (typeGuard) {
          const elseifScope = pushScope();
          applyElseBranchGuard(elseifScope, typeGuard, lookupVariable);
          visit(elseNode, node);
          popScope();
        } else {
          visit(elseNode, node);
        }
      }
      
      // When the if-branch is an early-exit type guard (always returns) with no else,
      // the code after this block is only reachable when the condition was false.
      // Apply type exclusion to the outer scope so subsequent statements see the narrowed type.
      // Note: the parser always creates an else_if node (matched with empty rule ['w?']) even
      // when there is no actual else clause, so we must check for meaningful else content.
      const ifBranchAlwaysReturns = returnsAfterIf > returnsBeforeIf;
      const elseHasContent = elseNode && (
        elseNode.named?.exp ||
        (elseNode.named?.stats && elseNode.named.stats.length > 0) ||
        elseNode.named?.elseif
      );
      if (typeGuard && !elseHasContent && ifBranchAlwaysReturns) {
        applyPostIfGuard(getCurrentScope(), typeGuard, lookupVariable);
      }
      
      pushToParent(node, parent);
    },

    else_if: (node, parent) => {
      const { pushScope, popScope, lookupVariable } = getState();

      // Simple else branch: no condition, just visit body
      if (!node.named?.exp) {
        node.named?.stats?.forEach(stat => visit(stat, node));
        if (node.named?.elseif) {
          visit(node.named.elseif, node);
        }
        pushToParent(node, parent);
        return;
      }

      // Visit the elseif condition expression
      visit(node.named.exp, node);

      // Apply type narrowing for this elseif's own condition
      const typeGuard = detectTypeofCheck(node.named.exp) || detectEqualityCheck(node.named.exp)
        || detectTruthinessCheck(node.named.exp) || detectPredicateGuard(node.named.exp);
      if (typeGuard) {
        const ifScope = pushScope();
        applyIfBranchGuard(ifScope, typeGuard, lookupVariable);
        node.named.stats?.forEach(stat => visit(stat, node));
        popScope();
      } else {
        node.named.stats?.forEach(stat => visit(stat, node));
      }

      // Visit chained else_if, applying exclusion from this branch's typeGuard
      if (node.named.elseif) {
        if (typeGuard) {
          const elseScope = pushScope();
          applyElseBranchGuard(elseScope, typeGuard, lookupVariable);
          visit(node.named.elseif, node);
          popScope();
        } else {
          visit(node.named.elseif, node);
        }
      }

      pushToParent(node, parent);
    },

    assign_op: (node, parent) => {
      const { lookupVariable, pushWarning, typeAliases, inferencePhase } = getState();

      // Always visit children so that expression inference is populated
      visitChildren(node);

      // Type-checking only happens in the checking phase.
      // Also skip property-access variants (e.g. obj.prop += 1) — those
      // require resolving the property type from the object, which is handled
      // by the property-access validators elsewhere.
      if (inferencePhase !== 'checking' || node.named.target) {
        pushToParent(node, parent);
        return;
      }

      const varName = node.named.name?.value;
      const expNode = node.named.exp;

      if (!varName || !expNode) {
        pushToParent(node, parent);
        return;
      }

      // Find the assign_operator token (e.g. '+=', '-=', '*=', '/=')
      const assignOperator = node.children?.find(c => c.type === 'assign_operator');
      if (!assignOperator) {
        pushToParent(node, parent);
        return;
      }

      // Strip the trailing '=' to get the base arithmetic operator
      const opChar = assignOperator.value.slice(0, -1); // '+=' -> '+'

      // Look up the variable's current type
      const varDef = lookupVariable(varName);
      if (!varDef || !varDef.type) {
        pushToParent(node, parent);
        return;
      }

      const varType = resolveTypeAlias(varDef.type, typeAliases);
      const rhsType = expNode.inference?.[0];

      if (!rhsType || rhsType === AnyType || varType === AnyType) {
        pushToParent(node, parent);
        return;
      }

      // Validate the compound assignment using the same math-operation rules
      const result = TypeChecker.checkMathOperation(varType, rhsType, opChar);
      if (!result.valid) {
        const msgs = result.warning ? [result.warning] : (result.warnings ?? []);
        msgs.forEach(msg => pushWarning(node, msg));
      }

      pushToParent(node, parent);
    },

    while_loop: (node, parent) => {
      const { pushScope, popScope, lookupVariable, pushWarning, typeAliases } = getState();

      // Check for impossible comparisons in the condition
      const impossibleComparison = detectImpossibleComparison(node.named.exp, lookupVariable, typeAliases);
      if (impossibleComparison) {
        const { variable, comparedValue, possibleValues } = impossibleComparison;
        pushWarning(
          node.named.exp,
          `This condition will always be false: '${variable}' has type ${possibleValues.join(' | ')} and can never equal ${comparedValue}`
        );
      }

      // Detect type guards in the loop condition (same patterns as `condition` handler)
      const typeGuard = detectTypeofCheck(node.named.exp) || detectEqualityCheck(node.named.exp) || detectTruthinessCheck(node.named.exp);

      // Visit condition expression
      if (node.named.exp) {
        visit(node.named.exp, node);
      }

      // Push an isolated scope for the loop body (mirrors for_loop / condition)
      const scope = pushScope();

      // Apply type narrowing from the condition into the body
      if (typeGuard) {
        applyIfBranchGuard(scope, typeGuard, lookupVariable);
      }

      // Visit body statements
      if (node.named.stats) {
        node.named.stats.forEach(stat => visit(stat, node));
      }

      popScope();
      pushToParent(node, parent);
    },

    for_loop: (node, parent) => {
      const { pushScope, popScope, pushWarning, typeAliases, inferencePhase } = getState();
      const scope = pushScope();
      
      // Get variable names
      const key = (node.named.key && node.named.key.value) || null;
      const value = node.named.value ? node.named.value.value : null;
      
      // Check for 'of' keyword (array iteration mode)
      const isArray = !!(node.named.of)
      
      // Key type: number with :array, string without (Object.keys returns strings)
      const keyType = isArray ? NumberType : StringType;
      
      // Add variables to scope with their types
      if (key) {
        scope[key] = { type: keyType, node: node.named.key };
        if (inferencePhase === 'inference') {
          node.named.key.inferredType = keyType;
        }
      }
      
      // Visit the expression being iterated
      if (node.named.exp) {
        visit(node.named.exp, node);
        
        // Check if we're iterating an array without :array annotation
        const expType = node.named.exp.inference?.[0];
        if (expType && key && !isArray) {
          const resolvedExpType = resolveTypeAlias(expType, typeAliases);
          const isArrayType = resolvedExpType instanceof ArrayType;
          
          if (isArrayType) {
            pushWarning(
              node.named.exp,
              `Iterating array without 'of' keyword - variable '${key}' will be string ("0", "1", ...) instead of number. Add 'of' after the expression to fix this.`
            );
          }
        }
      }
      
      // Infer value type if possible
      if (value && node.named.exp && node.named.exp.inference) {
        const expType = node.named.exp.inference[0];
        let valueType = AnyType;
        
        const resolvedExpType = resolveTypeAlias(expType, typeAliases);
        if (resolvedExpType instanceof ArrayType) {
          valueType = resolvedExpType.elementType;
        } else if (!isArray && resolvedExpType instanceof ObjectType && resolvedExpType.properties.size > 0) {
          // for key, value in obj — value is the union of all property value types
          const propTypes = Array.from(resolvedExpType.properties.values()).map(p => p.type);
          const unique = [...new Map(propTypes.map(t => [t.toString(), t])).values()];
          valueType = unique.length === 1 ? unique[0] : createUnionType(unique);
        }
        
        scope[value] = { type: valueType, node: node.named.value };
        if (inferencePhase === 'inference') {
          node.named.value.inferredType = valueType;
        }
      }
      
      // Visit loop body statements
      if (node.named.stats) {
        node.named.stats.forEach(stat => visit(stat, node));
      }
      
      popScope();
      pushToParent(node, parent);
    },

    try_catch: (node, parent) => {
      const { pushScope, popScope } = getState();

      // Visit try body in an isolated scope so variables declared inside
      // do not leak into the surrounding scope.
      pushScope();
      if (node.named.statstry) {
        node.named.statstry.forEach(stat => visit(stat, node));
      }
      popScope();

      // Visit catch body in an isolated scope.  The catch variable (e.g. `err`
      // in `catch err { ... }`) is bound here as AnyType because the runtime
      // exception value has no static type information.
      const catchScope = pushScope();
      const catchVarName = node.named.name?.value;
      if (catchVarName) {
        catchScope[catchVarName] = { type: AnyType, node: node.named.name };
      }
      if (node.named.statscatch) {
        node.named.statscatch.forEach(stat => visit(stat, node));
      }
      popScope();

      pushToParent(node, parent);
    },
  };
}

export default createStatementHandlers;
